"use client";

import { useEffect, useState } from "react";
import type { ReferencePeptide } from "@/lib/research/types";
import { HeroCompoundView } from "@/ui/components/marketing/hero-compound-view";

export type HeroCyclerEntry = {
	peptide: ReferencePeptide;
	imageSrc: string | null;
	href?: string;
};

interface HeroCyclerProps {
	entries: HeroCyclerEntry[];
	intervalMs?: number;
	featuredLabel?: string;
	showPeptideAria?: string;
	sequenceLabel?: string;
	originLabel?: string;
}

export function HeroCycler({
	entries,
	intervalMs = 4500,
	featuredLabel,
	showPeptideAria = "Show peptide",
	sequenceLabel,
	originLabel,
}: HeroCyclerProps) {
	const [idx, setIdx] = useState(0);
	const [paused, setPaused] = useState(false);

	useEffect(() => {
		if (paused || entries.length < 2) return;
		const reduced =
			typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
		if (reduced) return;
		const id = setInterval(() => setIdx((i) => (i + 1) % entries.length), intervalMs);
		return () => clearInterval(id);
	}, [paused, entries.length, intervalMs]);

	if (entries.length === 0) return null;

	return (
		<div className="relative" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
			<div className="relative aspect-square w-full">
				{entries.map((entry, i) => (
					<div
						key={entry.peptide.code}
						className={`absolute inset-0 transition-opacity duration-700 ease-out ${
							i === idx ? "z-10 opacity-100" : "pointer-events-none opacity-0"
						}`}
						aria-hidden={i !== idx}
					>
						<HeroCompoundView
							peptide={entry.peptide}
							imageSrc={entry.imageSrc}
							href={entry.href}
							featuredLabel={featuredLabel}
							sequenceLabel={sequenceLabel}
							originLabel={originLabel}
						/>
					</div>
				))}
			</div>

			<div className="mt-5 flex items-center gap-3">
				<span className="font-mono text-[0.62rem] uppercase tracking-[0.18em] text-muted-foreground">
					{String(idx + 1).padStart(2, "0")} / {String(entries.length).padStart(2, "0")}
				</span>
				<div className="flex flex-1 gap-1">
					{entries.map((_, i) => (
						<button
							key={i}
							type="button"
							onClick={() => setIdx(i)}
							aria-label={`${showPeptideAria} ${i + 1}`}
							className={`h-px flex-1 transition-all duration-500 ${
								i === idx ? "h-[2px] bg-primary" : "hover:bg-muted-foreground/60 bg-border"
							}`}
						/>
					))}
				</div>
				<span className="font-mono text-[0.62rem] uppercase tracking-[0.18em] text-primary">
					{entries[idx].peptide.code}
				</span>
			</div>
		</div>
	);
}
